import { useEffect, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { OrderItem } from "@/components/order/OrderItem";
import { SearchForm } from "@/components/common/SearchForm";
import { Toggle } from "@/components/ui/toggle";
import { Dropdown } from "@/components/ui/select";
import { OrderInfo } from "@/components/order/OrderInfo";
import { Trash2, Plus, ArrowDownNarrowWide, ArrowUpWideNarrow, LoaderCircle } from "lucide-react";
import { SortCategory } from "@/helper/sort";
import { OrderInfoSchema, OrderSchema } from "@/components/order/OrderSchema";
import { sortData } from "@/helper/sort";
import { OptionSet } from "@/components/ui/optionset";
import { useApiFetch } from "@/helper/apiFetch";
import { useAuth } from "@/auth";
import { useTranslate } from "@/helper/translate";
import { z } from "zod";


type Lookup = { id: string; name: string };


// Utility: Filter search input
function applySearchFilter(data: OrderInfoSchema[], query: string): OrderInfoSchema[] {
  const term = query.trim().toLowerCase();
  if (term == '') return data;
  if (!data) return data;
  return data.filter(item =>
    item.customer.toLowerCase().includes(term) ||
    (item.orderName ?? "").toLowerCase().includes(term)
  );
}

// Component
export function OrderPage() {

  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  const [isLoaded, setIsLoaded] = useState(false);

  const { linkedCustomerId } = useAuth();
  const [customers, setCustomers] = useState<Lookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);
  const [orders, setOrders] = useState<OrderInfoSchema[]>([]);
  const [onlyActive, setActive] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [sortCategory, setSortCategory] = useState<SortCategory>();
  const [searchText, setSearchText] = useState<string>("");
  const [editOrderId, setEditOrder] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<string>(linkedCustomerId != "Admin" ? linkedCustomerId : "");
  const [selectedViewOption, setViewOption] = useState<number>(1); // 1 for Customer, 2 for Site

  const viewOptions = [
    { value: 1, label: translate("ui.customer") },
    { value: 2, label: translate("ui.site") }
  ];


  const colHeaders =(linkedCustomerId == "Admin")?
   [{ name: "Select", sort: "" }, { name: "Site", sort: "site" }, { name: "Customer", sort: "customer" }, { name: "Name", sort: "orderName" }, { name: "Start Date", sort: "orderBegin" }, { name: "End Date", sort: "orderEnd" }, { name: "Rotation", sort: "rotation" }, { name: "Duration", sort: "orderLength" }, { name: "Container", sort: "container" }]
   :
  [{ name: "Site", sort: "site" }, { name: "Name", sort: "orderName" }, { name: "Start Date", sort: "orderBegin" }, { name: "End Date", sort: "orderEnd" }, { name: "Rotation", sort: "rotation" }, { name: "Duration", sort: "orderLength" }];

  
  const handleSortCategoryChange = (newCategory: string | number) => {
    const newC: SortCategory = {
      name: newCategory,
      direction: sortCategory?.name === newCategory ? !sortCategory?.direction : true
    }

    setSortCategory(newC);
    setOrders(sortData(orders, newC));
  };

  const handleCheckChange = (id: string, checked: boolean) => {
    setSelectedOrders(prev =>
      checked ? [...prev, id] : prev.filter(spotId => spotId !== id)
    );
  };

  const deleteOrders = () => {

    selectedOrders.map(orderId => {
      apiFetch(`/App/DeleteOrder?orderId=${orderId}`, {
        method: "GET"
      }).then(res => {
        if (res.ok) {
          //remove from array
          setOrders(prev => prev.filter(s => s.orderId !== orderId));
          setSelectedOrders(prev => prev.filter(s => s !== orderId));

        }
        else {
          res.text().then(text => {
            setOrders((prev) =>
              prev.map((s) =>
                s.orderId === orderId ? { ...s, error: text } : s
              )
            );
          })
        }
      }).catch(err => {
        console.error(err);
      })
    });


  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const input = form.querySelector("input");
    setSearchText(input?.value.toLowerCase() || "");
  };

  const filteredData = applySearchFilter(orders, searchText);

  const now = new Date();
  const orderItems = filteredData && filteredData.map((item, index) => (
    item.isDeleted || (onlyActive && new Date(item.orderEnd) < now) ? null : (
      <OrderItem
        key={index}
        item={item}
        viewMode={selectedViewOption}
        isSelected={selectedOrders.includes(item.orderId)}
        showCustomer={linkedCustomerId == "Admin"}
        onClick={() => linkedCustomerId == "Admin" && setEditOrder(item.orderId)}
        onCheckChange={handleCheckChange}
      />
    )
  ));

  useEffect(() => {
if (linkedCustomerId == "Admin")
{
    apiFetch(`/App/GetSiteLookup`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch sites");
        return res.json();
      })
      .then((data) => {
        setSites(data);
        if (data.length > 0) {
          setSelectedSite(data[0].id);
        }
      })
      .catch((err) => {
        console.error(err);
      });
    }
   
  }, [linkedCustomerId]);


  useEffect(() => {
   

    if (linkedCustomerId != "Admin") {
     
      setCustomers([
        { id: linkedCustomerId, name: "" }
      ]);
      setSelectedCustomer(linkedCustomerId);

    }
    else {
       if (sites.length == 0)//user is not admin //TODO MANU
        {
          return;
        }

      apiFetch(`/App/GetCustomerLookup`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch customers");
          return res.json();
        })
        .then((data) => {
          setCustomers(data);
          if (data.length > 0) {
            setSelectedCustomer(data[0].id); // Adjust based on ID field
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }

  }, [sites, selectedViewOption]);

  useEffect(() => {
    setIsLoaded(false);
    if (selectedSite && selectedSite != "" || selectedCustomer && selectedCustomer != "") {
      apiFetch(`/App/GetOrders?${selectedViewOption == 1 ? "CustomerId=" + selectedCustomer : "SiteId=" + selectedSite}&active=${onlyActive}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch spots");
          return res.json();
        })
        .then((orderData) => {
          setOrders(orderData);
          setIsLoaded(true);

        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [selectedSite, selectedCustomer, onlyActive, refreshCount, selectedViewOption]);

  return (
    <div className="h-[calc(100vh-60px)] flex flex-col">
      <div className="content-header flex justify-between pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
        <div className="flex items-center gap-4 min-w-48">
          <h1 className="font-bold text-2xl">{translate("ui.orders")}</h1>
          {linkedCustomerId == "Admin" && (
            <Plus className="lucide lucide-plus w-7 h-7 cursor-pointer bg-sky-600 hover:bg-sky-700 dark:bg-orange-400 dark:hover:bg-orange-500 rounded-full text-white dark:text-black" onClick={() => setEditOrder("")} />
          )}
          {(selectedOrders.length > 0) &&
            <Trash2 className="w-7 h-7 cursor-pointer bg-rose-600 hover:bg-rose-700 rounded-full text-white p-[3px]" onClick={deleteOrders} />
          }

        </div>
{linkedCustomerId == "Admin" && (
        <div className="mx-8 flex items-center gap-4">
          
            <OptionSet
              options={viewOptions}
              value={selectedViewOption}
              onChange={(e) => { setViewOption(e); }}
            />
         
          
          {selectedViewOption === 1 ? (
            <Dropdown
              source={customers}
              value={selectedCustomer}
              valueMember={"id"}
              displayMember={"name"}
              onChange={(value) => setSelectedCustomer(value as string)}
              className="min-w-80"
            />
          ) : (
            <Dropdown
              source={sites}
              value={selectedSite}
              valueMember={"id"}
              displayMember={"name"}
              onChange={(value) => setSelectedSite(value as string)}
              className="min-w-80"
            />
          )}

        </div>
 )}

        <SearchForm title={translate("ui.searchorder")} onSubmit={handleSearchSubmit} />

        <div className="flex items-center gap-1">
          <Toggle
            title={translate("ui.excludeorders")}
            checked={onlyActive}
            onChange={(e) => setActive(e.target.checked)}
          />


        </div>
      </div>

      {!isLoaded ? (
                <div className="w-full text-center my-10 flex items-center justify-center">{translate("common.loading")} <LoaderCircle className="ml-3 w-5 h-5 inline-block animate-spin" /></div>
      ) : (
        <div className="flex-1 overflow-auto px-10">

          <div className="overflow-x-auto my-10 border rounded-xl shadow-2xl border-gray-100 dark:border-zinc-600">
            <table className="table-auto w-full border-collapse">
              <thead className="border-b border-gray-300 text-sm text-gray-600 dark:text-gray-50 dark:border-zinc-900">
                <tr>
                  {colHeaders.map((header, index) => {

                    const shouldShow =
                      (selectedViewOption === 1 && header.name !== "Customer") ||
                      (selectedViewOption === 2 && header.name !== "Site" && (linkedCustomerId == "Admin" || (header.name !== "Customer" && header.name !== "Select")));

                    if (!shouldShow) return null;

                    return (
                      <th
                        key={index}
                        className="px-4 py-2 text-left cursor-pointer bg-gray-200 dark:bg-zinc-900"
                        onClick={() => handleSortCategoryChange(header.sort)}
                      >
                        <div className=" flex items-center">
                          {translate("ui." + header.name.toLowerCase())}
                          {sortCategory?.name === header.sort ? (
                            sortCategory.direction ? (
                              <ArrowDownNarrowWide className="w-3 h-3" />
                            ) : (
                              <ArrowUpWideNarrow className="w-3 h-3" />
                            )
                          ) : <div className="w-3" />}
                        </div>
                      </th>)
                  })}
                </tr>

              </thead>
              <tbody>{orderItems}</tbody>
            </table>
          </div>

        </div>)}




      <Popup
        isOpen={editOrderId !== null}
        onClose={() => setEditOrder(null)}
        title={translate("ui.orderinfo")}
         className="w-[800px]"
      >
        <OrderInfo onClose={(order: string|null) => { setEditOrder(null); if (order) { setRefreshCount(refreshCount + 1); } }} Id={editOrderId ?? ""} CustomerId={selectedViewOption == 1 ? selectedCustomer : undefined} SiteId={selectedViewOption == 2 ? selectedSite : undefined} />
      </Popup>
    </div>
  );
}
